import React, { useState } from "react"
import type { BasePolicy } from "@/models/api_resource"
import type { BinaryRestriction, BinaryRestrictionMeta, Mode } from "./models"
import {
  BinaryFormContext,
  FIELD_PREFIX,
  MODE_ALLOW,
  MODE_DENY,
} from "./models"
import FieldErrors from "../shared/field_errors"
import InfoTooltip from "../shared/info_tooltip"
import PlatformLabel from "../shared/platform_label"
import Checkbox from "../forms/checkbox"
import RadioButtonGroup from "../forms/radio_button_group"
import BinaryRuleList from "./binary_rule_list"

const MANAGED_APPS_FIELD = `${FIELD_PREFIX}[managed_apps_always_allowed]`
const APPLE_APPS_FIELD = `${FIELD_PREFIX}[apple_apps_always_allowed]`
const SIMPLEMDM_IOS_FIELD = `${FIELD_PREFIX}[simplemdm_ios_always_allowed]`
const SIMPLEMDM_MACOS_FIELD = `${FIELD_PREFIX}[simplemdm_macos_always_allowed]`
const SIMPLEMDM_INTUNE_FIELD = `${FIELD_PREFIX}[simplemdm_intune_always_allowed]`
const MODE_FIELD = `${FIELD_PREFIX}[mode]`

const APPLE_APPS_NOTE_ID = "binary-restriction-apple-apps-note"
const SIMPLEMDM_IOS_NOTE_ID = "binary-restriction-simplemdm-ios-note"
const SIMPLEMDM_MACOS_NOTE_ID = "binary-restriction-simplemdm-macos-note"
const SIMPLEMDM_INTUNE_NOTE_ID = "binary-restriction-simplemdm-intune-note"

interface ModeOption {
  text: React.ReactNode
  value: Mode
}

const MODE_OPTIONS: ModeOption[] = [
  {
    text: (
      <>
        <strong>Denied Binaries</strong> — block any binary matching a rule
        (recommended)
      </>
    ),
    value: MODE_DENY,
  },
  {
    text: (
      <>
        <strong>Allowed Binaries</strong> — only binaries matching a rule may
        launch
      </>
    ),
    value: MODE_ALLOW,
  },
]

interface BinaryRulesFormProps {
  data: BinaryRestriction
  policy: BasePolicy
  meta: BinaryRestrictionMeta
}

export default function BinaryRulesForm({
  data,
  policy,
  meta,
}: BinaryRulesFormProps) {
  const [mode, setMode] = useState<Mode>(data.mode || MODE_DENY)
  const [managedAppsAlwaysAllowed, setManagedAppsAlwaysAllowed] =
    useState<boolean>(data.managedAppsAlwaysAllowed ?? false)
  const [appleAppsAlwaysAllowed, setAppleAppsAlwaysAllowed] = useState<boolean>(
    data.appleAppsAlwaysAllowed ?? true,
  )
  const [simplemdmIosAlwaysAllowed, setSimplemdmIosAlwaysAllowed] =
    useState<boolean>(data.simplemdmIosAlwaysAllowed ?? true)
  const [simplemdmMacosAlwaysAllowed, setSimplemdmMacosAlwaysAllowed] =
    useState<boolean>(data.simplemdmMacosAlwaysAllowed ?? true)
  const [simplemdmIntuneAlwaysAllowed, setSimplemdmIntuneAlwaysAllowed] =
    useState<boolean>(data.simplemdmIntuneAlwaysAllowed ?? true)

  const disabled = !policy.update

  return (
    <BinaryFormContext.Provider
      value={{
        mode,
        disabled,
        signingStateOptions: meta.signingStateOptions,
        tooltips: meta.tooltips,
      }}
    >
      <div className="binary-rules-form">
        <h3>Restriction Mode</h3>
        <p>
          Choose whether the rules below describe binaries that may launch, or
          binaries that are blocked.
        </p>

        <RadioButtonGroup
          name={MODE_FIELD}
          value={mode}
          options={MODE_OPTIONS}
          disabled={disabled}
          onChange={(option: ModeOption) => setMode(option.value)}
        />

        {mode === MODE_DENY && (
          <p className="text-muted">
            <strong>Denied Binaries:</strong> if present, the device doesn't
            allow binaries that match a rule to run. Unless a rule identifies an
            app by Bundle ID, it must specify a Code Directory Hash, Team ID, or
            Signing ID.
          </p>
        )}

        {mode === MODE_ALLOW && (
          <>
            <p className="text-muted">
              <strong>Allowed Binaries:</strong> if present, the device only
              allows binaries that match a rule to run. The device always runs
              system-critical processes. Unless a rule identifies an app by
              Bundle ID, it must specify a Code Directory Hash or Team ID.
            </p>
            <div className="alert alert-warning">
              <strong>Caution:</strong> In Allowed mode, only binaries matching
              a rule — plus managed apps (if enabled above) and system-critical
              processes — can launch. An incomplete allow list can prevent
              essential apps from running and lock users out. Test on a small
              group before deploying widely.
            </div>

            <input type="hidden" name={MANAGED_APPS_FIELD} value="0" />
            <Checkbox
              name={MANAGED_APPS_FIELD}
              value="1"
              checked={managedAppsAlwaysAllowed}
              disabled={disabled}
              offsetInput={false}
              hint={meta.tooltips.alwaysAllowManagedApps}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setManagedAppsAlwaysAllowed(event.target.checked)
              }
            >
              Always allow managed apps <PlatformLabel>macOS</PlatformLabel>
            </Checkbox>

            <input type="hidden" name={APPLE_APPS_FIELD} value="0" />
            <Checkbox
              name={APPLE_APPS_FIELD}
              value="1"
              checked={appleAppsAlwaysAllowed}
              disabled={disabled}
              offsetInput={false}
              describedBy={APPLE_APPS_NOTE_ID}
              afterLabel={
                <InfoTooltip
                  id={APPLE_APPS_NOTE_ID}
                  message={meta.tooltips.appleAppsCaution}
                />
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setAppleAppsAlwaysAllowed(event.target.checked)
              }
            >
              Always allow Apple system apps{" "}
              <PlatformLabel>macOS</PlatformLabel>
            </Checkbox>

            {!appleAppsAlwaysAllowed && (
              <div className="alert alert-warning">
                <strong>Caution:</strong> {meta.tooltips.appleAppsCaution}
              </div>
            )}

            <input type="hidden" name={SIMPLEMDM_IOS_FIELD} value="0" />
            <Checkbox
              name={SIMPLEMDM_IOS_FIELD}
              value="1"
              checked={simplemdmIosAlwaysAllowed}
              disabled={disabled}
              offsetInput={false}
              describedBy={SIMPLEMDM_IOS_NOTE_ID}
              afterLabel={
                <InfoTooltip
                  id={SIMPLEMDM_IOS_NOTE_ID}
                  message={meta.tooltips.simplemdmIosCaution}
                />
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSimplemdmIosAlwaysAllowed(event.target.checked)
              }
            >
              Allow SimpleMDM iOS app <PlatformLabel>iOS</PlatformLabel>
            </Checkbox>
            {!simplemdmIosAlwaysAllowed && (
              <div className="alert alert-warning">
                <strong>Caution:</strong> {meta.tooltips.simplemdmIosCaution}
              </div>
            )}

            <input type="hidden" name={SIMPLEMDM_MACOS_FIELD} value="0" />
            <Checkbox
              name={SIMPLEMDM_MACOS_FIELD}
              value="1"
              checked={simplemdmMacosAlwaysAllowed}
              disabled={disabled}
              offsetInput={false}
              describedBy={SIMPLEMDM_MACOS_NOTE_ID}
              afterLabel={
                <InfoTooltip
                  id={SIMPLEMDM_MACOS_NOTE_ID}
                  message={meta.tooltips.simplemdmMacosCaution}
                />
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSimplemdmMacosAlwaysAllowed(event.target.checked)
              }
            >
              Allow SimpleMDM Munki agent{" "}
              <PlatformLabel>macOS</PlatformLabel>
            </Checkbox>
            {!simplemdmMacosAlwaysAllowed && (
              <div className="alert alert-warning">
                <strong>Caution:</strong> {meta.tooltips.simplemdmMacosCaution}
              </div>
            )}

            <input type="hidden" name={SIMPLEMDM_INTUNE_FIELD} value="0" />
            <Checkbox
              name={SIMPLEMDM_INTUNE_FIELD}
              value="1"
              checked={simplemdmIntuneAlwaysAllowed}
              disabled={disabled}
              offsetInput={false}
              describedBy={SIMPLEMDM_INTUNE_NOTE_ID}
              afterLabel={
                <InfoTooltip
                  id={SIMPLEMDM_INTUNE_NOTE_ID}
                  message={meta.tooltips.simplemdmIntuneCaution}
                />
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSimplemdmIntuneAlwaysAllowed(event.target.checked)
              }
            >
              Allow SimpleMDM & Microsoft apps for Device Compliance integration{" "}
              <PlatformLabel>macOS</PlatformLabel>
              {" "}
              <PlatformLabel>iOS</PlatformLabel>
            </Checkbox>
            {!simplemdmIntuneAlwaysAllowed && (
              <div className="alert alert-warning">
                <strong>Caution:</strong> {meta.tooltips.simplemdmIntuneCaution}
              </div>
            )}
          </>
        )}

        <h3 className="binary-rules-header">Binary Rules</h3>
        <FieldErrors
          errors={data.errors?.binaryRules}
          className="binary-rule-errors"
        />
        <p className="text-muted">
          Each rule identifies a binary using one or more signing properties. A
          binary only matches when all the binary identifiers match. Use{" "}
          <code>codesign -dvvv &lt;path_to_binary&gt;</code> to show the
          information you need to generate these values.
        </p>

        <BinaryRuleList data={data} />
      </div>
    </BinaryFormContext.Provider>
  )
}
